import React, { useEffect, useMemo, useState } from 'react';
import Button from '../Button';

export type ReelUploadModalPhase = 'preview' | 'uploading' | 'success' | 'error';
export type ReelUploadModalMode = 'upload' | 'attach';

export interface ReelUploadModalProps {
  show: boolean;
  file: File | null;
  phase: ReelUploadModalPhase;
  mode?: ReelUploadModalMode;
  title?: string;
  confirmLabel?: string;
  error?: string | null;
  onShare: () => void;
  onCancel: () => void;
  onRetry: () => void;
}

/**
 * Preview + upload progress modal for gönderi/reel creation.
 * Uses a local blob URL for pre-upload preview; never renders video without a valid src.
 */
const ReelUploadModal: React.FC<ReelUploadModalProps> = ({
  show,
  file,
  phase,
  mode = 'upload',
  title,
  confirmLabel,
  error = null,
  onShare,
  onCancel,
  onRetry,
}) => {
  const [previewReady, setPreviewReady] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const previewUrl = useMemo(() => {
    if (!file || !show) return null;
    return URL.createObjectURL(file);
  }, [file, show]);

  useEffect(() => {
    setPreviewReady(false);
    setPreviewError(false);
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!show || !file) return null;

  const isBusy = phase === 'uploading';
  const canClose = !isBusy;
  const canShare = phase === 'preview' || phase === 'error';
  const showPreviewVideo = previewUrl && !previewError;

  const isAttachMode = mode === 'attach';
  const modalTitle = title ?? (isAttachMode ? 'Reel ekle' : 'Gönderi yükle');
  const primaryLabel = confirmLabel ?? (isAttachMode ? 'Ekle' : 'Paylaş');

  const statusMessage = (() => {
    if (phase === 'uploading') return isAttachMode ? 'Reel hazırlanıyor…' : 'Gönderi yükleniyor…';
    if (phase === 'success') return 'Gönderi başarıyla oluşturuldu.';
    if (phase === 'error') return error ?? 'Yükleme başarısız oldu';
    if (previewError) return 'Önizleme yüklenemedi.';
    if (!previewReady && showPreviewVideo) return 'Önizleme hazırlanıyor…';
    return null;
  })();

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        aria-labelledby="reel-upload-modal-title"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="reel-upload-modal-title">
                {modalTitle}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onCancel}
                disabled={!canClose}
                aria-label="Kapat"
              />
            </div>
            <div className="modal-body">
              <div
                className="reel-upload-preview bg-dark rounded overflow-hidden position-relative d-flex align-items-center justify-content-center"
                style={{ minHeight: 280, maxHeight: '50vh' }}
              >
                {showPreviewVideo ? (
                  <>
                    {!previewReady && (
                      <div className="position-absolute top-50 start-50 translate-middle text-center text-white-50">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Önizleme hazırlanıyor…
                      </div>
                    )}
                    <video
                      src={previewUrl}
                      muted
                      playsInline
                      controls
                      preload="metadata"
                      onLoadedMetadata={() => setPreviewReady(true)}
                      onCanPlay={() => setPreviewReady(true)}
                      onError={() => {
                        setPreviewError(true);
                        setPreviewReady(false);
                      }}
                      style={{
                        width: '100%',
                        maxHeight: '50vh',
                        objectFit: 'contain',
                        display: previewReady ? 'block' : 'none',
                      }}
                    />
                  </>
                ) : (
                  <div className="text-center text-white-50 p-4">
                    <i className="fas fa-video fa-2x mb-2" aria-hidden="true" />
                    <p className="mb-0 small">{file.name}</p>
                  </div>
                )}

                {isBusy && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                    style={{ background: 'rgba(0,0,0,0.65)' }}
                  >
                    <span className="spinner-border text-light mb-2" role="status" aria-hidden="true" />
                    <span className="text-white small">{isAttachMode ? 'Reel ekleniyor…' : 'Gönderi yükleniyor…'}</span>
                  </div>
                )}
              </div>

              <p className="text-muted small mt-2 mb-0 text-truncate" title={file.name}>
                {file.name}
              </p>

              {statusMessage && (
                <div
                  className={`alert small mt-3 mb-0 ${
                    phase === 'error' || previewError ? 'alert-danger' : phase === 'success' ? 'alert-success' : 'alert-info'
                  }`}
                  role="status"
                >
                  {statusMessage}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onCancel}
                disabled={!canClose}
              >
                İptal
              </Button>
              {phase === 'error' && (
                <Button type="button" className="btn btn-outline-primary" onClick={onRetry} disabled={isBusy}>
                  Tekrar dene
                </Button>
              )}
              {canShare && (
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={onShare}
                  disabled={
                    isBusy || Boolean(showPreviewVideo && !previewReady && !previewError)
                  }
                >
                  {primaryLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default ReelUploadModal;
